import { useQueryParamKey } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import { Popover, Typography } from '@cobliteam/ui/components';
import SelectTimeWindow from '@cobliteam/ui-business/components/Selects/SelectTimeWindow/SelectTimeWindow';
import { format } from '@cobliteam/utils/date';
import { parseAsTimestamp, useQueryStates } from 'nuqs';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

interface TrackingTimeFilterProps {
  tripMaxTime: number;
  tripMinTime: number;
  onTimeChange: (tripStartTime: number, tripEndTime: number) => void;
}

const trackingTimeParsers = {
  start_time: parseAsTimestamp,
  end_time: parseAsTimestamp,
  start_date: parseAsTimestamp,
  end_date: parseAsTimestamp,
};

export const TrackingTimeFilter = ({ tripMaxTime, tripMinTime, onTimeChange }: TrackingTimeFilterProps) => {
  const { t } = useTranslation('tracking');
  const { paramKey: eventTime } = useQueryParamKey('event_time');
  const [selectedRange, setSelectedRange] = useState<[number, number] | undefined>(undefined);
  const processedEventTimeRef = useRef<string | null>(null);
  const [{ start_time: urlStartTime, end_time: urlEndTime, start_date: urlStartDate, end_date: urlEndDate }, setTimeRange] = useQueryStates(trackingTimeParsers, {
    throttleMs: 200,
  });
  const appliedUrlValuesRef = useRef<{ start: number; end: number } | null>(null);

  // Prefer the new start_time/end_time params; fall back to legacy start_date/end_date.
  const urlStart = urlStartTime ?? urlStartDate;
  const urlEnd = urlEndTime ?? urlEndDate;

  useEffect(() => {
    if (eventTime && processedEventTimeRef.current !== eventTime) {
      const start = Number(eventTime) - 1 * 60 * 1000; // 1 minute before
      const end = Number(eventTime) + 1 * 60 * 1000; // 1 minute after
      setSelectedRange([start, end]);
      onTimeChange(start, end);
      processedEventTimeRef.current = eventTime as string | null;
    }
  }, [eventTime, onTimeChange]);

  // Apply start_time/end_time (or legacy start_date/end_date) from URL once the trip window is known.
  useEffect(() => {
    if (!(urlStart && urlEnd)) return;

    const start = urlStart.valueOf();
    const end = urlEnd.valueOf();
    if (start < tripMinTime || end > tripMaxTime || start >= end) return;

    if (appliedUrlValuesRef.current?.start === start && appliedUrlValuesRef.current?.end === end) return;

    setSelectedRange([start, end]);
    onTimeChange(start, end);
    appliedUrlValuesRef.current = { start, end };
  }, [urlStart, urlEnd, tripMinTime, tripMaxTime, onTimeChange]);

  // prevent loops
  const handleTimeChange = useCallback(
    (start: number, end: number) => {
      const [currentStart, currentEnd] = selectedRange ?? [tripMinTime, tripMaxTime];
      if (start === currentStart && end === currentEnd) return;

      setSelectedRange([start, end]);
      onTimeChange(start, end);
      const isFullWindow = start <= tripMinTime && end >= tripMaxTime;
      appliedUrlValuesRef.current = isFullWindow ? null : { start, end };
      setTimeRange(
        isFullWindow
          ? { start_time: null, end_time: null, start_date: null, end_date: null }
          : { start_time: new Date(start), end_time: new Date(end), start_date: null, end_date: null }
      );
    },
    [selectedRange, tripMinTime, tripMaxTime, onTimeChange, setTimeRange]
  );

  return (
    <div className='mb-5 flex flex-col'>
      <div className='flex items-center'>
        <div className='flex items-center gap-1'>
          <Typography variant='text-md-semibold'>{t('route_time')}:</Typography>
          <Typography variant='text-md' className='font-normal'>
            {format(tripMinTime, 'HH:mm')} - {format(tripMaxTime, 'HH:mm')}
          </Typography>
        </div>
        <Popover>
          <Popover.Trigger>
            <Popover.Icon />
          </Popover.Trigger>
          <Popover.Content className='w-72'>
            <Typography variant='body'>
              {t('time_filter_will_be_applied')} <strong>{t('only_on_map')}</strong>. {t('to_analyze_route_sections_data_consult_report')}
            </Typography>
          </Popover.Content>
        </Popover>
      </div>

      <SelectTimeWindow size='sm' minStartTime={tripMinTime} maxEndTime={tripMaxTime} selectedTimeWindow={selectedRange} onTimeChange={handleTimeChange} showPresets={true} />
    </div>
  );
};

export default memo(TrackingTimeFilter);
